import type {
  CurrencyValue,
  FieldCodec
} from 'v5/core/entity-values/values.types';
import { customFieldColumns } from './customFieldColumns';
import { findCustomFieldRowForRead } from './customFieldRows';
import { validateField, validation } from './validation';

const MAX_DECIMALS = 2;
/** The amounts a CURRENCY field can hold. */
const CURRENCY_AMOUNT_RANGE = { min: -21_474_836.48, max: 21_474_836.47 };
/** Values within this distance of a whole number of the smallest unit count as whole. */
const EPSILON = 1e-6;

function isEmpty(value: CurrencyValue): boolean {
  return value.amount === null;
}

function hasAtMostTwoDecimals(amount: number): boolean {
  const scaled = amount * 10 ** MAX_DECIMALS;
  return Math.abs(scaled - Math.round(scaled)) <= EPSILON;
}

/**
 * CURRENCY custom field: `integer_value` holds the amount (123.45),
 * `string_value` the currency code.
 */
export const currencyCodec: FieldCodec<'currency'> = {
  decode(entity, spec, ctx) {
    const row = findCustomFieldRowForRead(entity, spec.definition.id, ctx);
    return {
      amount: row?.integer_value ?? null,
      currency: row?.string_value ?? ''
    };
  },
  encode(value) {
    return {
      type: 'custom',
      columns: customFieldColumns({
        integer_value: value.amount,
        string_value: value.currency === '' ? null : value.currency
      })
    };
  },
  empty() {
    return { amount: null, currency: '' };
  },
  isEmpty,
  validate(value, spec) {
    return validateField(value, spec, isEmpty, value => {
      if (value.amount !== null) {
        if (!Number.isFinite(value.amount)) {
          return validation.invalid({ code: 'invalid-amount', at: 'amount' });
        }
        if (!hasAtMostTwoDecimals(value.amount)) {
          return validation.invalid({
            code: 'too-many-decimals',
            max: MAX_DECIMALS,
            at: 'amount'
          });
        }
        if (
          value.amount < CURRENCY_AMOUNT_RANGE.min ||
          value.amount > CURRENCY_AMOUNT_RANGE.max
        ) {
          return validation.invalid({
            code: 'out-of-range',
            ...CURRENCY_AMOUNT_RANGE,
            at: 'amount'
          });
        }
      }
      if (value.amount !== null && value.currency === '') {
        return validation.invalid({
          code: 'part-required',
          part: 'currency',
          at: 'currency'
        });
      }
      return validation.valid();
    });
  }
};
